import type { DramaCard, DramaDetail, DramaEpisode, DramaSubtitleTrack, MemberDramaLibrary } from "@/api/member";

export interface CinemaEpisode {
  id: string;
  title: string;
  episodeNum: number;
  duration: string;
  videoUrl: string;
  quality: string;
  locked: boolean;
  cost: number;
  thumbnailUrl: string;
  subtitleTracks: DramaSubtitleTrack[];
}

export interface CinemaSeries {
  id: string;
  sourceType: "platform" | "creator";
  title: string;
  categoryId: number;
  categoryCode: string;
  category: string;
  description: string;
  coverUrl: string;
  carouselImageUrl: string;
  rating: number;
  totalEpisodes: number;
  unlockedCount: number;
  lastEpisodeNo: number;
  lastEpisodeTitle: string;
  views: number;
  weekViews: number;
  monthViews: number;
  isCarousel: boolean;
  isHot: boolean;
  hotRank: number;
  tag?: string;
  tagNew?: boolean;
  tagHot?: boolean;
  createdAt?: string;
  updatedAt?: string;
  latestEpisodeAt?: string;
  episodes: CinemaEpisode[];
}

function formatDuration(seconds?: number) {
  const value = Number(seconds) || 0;
  if (value <= 0) return "--:--";
  const minutes = Math.floor(value / 60);
  const remain = value % 60;
  return `${minutes}:${String(remain).padStart(2, "0")}`;
}

function categoryFromDrama(drama: Pick<DramaCard, "intro" | "shareTitle" | "shareDesc" | "categoryName" | "categoryCode">) {
  const categoryName = (drama.categoryName || "").trim();
  if (categoryName) return categoryName;
  return "短剧";
}

function isNewDrama(createdAt?: string) {
  if (!createdAt) return false;
  const timestamp = new Date(createdAt).getTime();
  if (!Number.isFinite(timestamp)) return false;
  const twoWeeks = 14 * 24 * 60 * 60 * 1000;
  return Date.now() - timestamp <= twoWeeks;
}

export function mapEpisode(episode: DramaEpisode, coverUrl: string): CinemaEpisode {
  return {
    id: String(episode.id),
    title: episode.title || `第${episode.episodeNo}集`,
    episodeNum: episode.episodeNo,
    duration: formatDuration(episode.durationSeconds),
    videoUrl: episode.videoUrl || "",
    quality: (episode.quality || "").trim(),
    locked: !episode.isUnlocked,
    cost: Number(episode.unlockPoints) || 0,
    thumbnailUrl: coverUrl,
    subtitleTracks: episode.subtitleTracks || [],
  };
}

export function mapDramaCard(drama: DramaCard | DramaDetail): CinemaSeries {
  const detail = drama as DramaDetail;
  const episodes = Array.isArray(detail.episodes)
    ? detail.episodes.map((episode) => mapEpisode(episode, drama.coverUrl))
    : drama.currentEpisode
      ? [mapEpisode(drama.currentEpisode, drama.coverUrl)]
      : [];

  return {
    id: String(drama.id),
    sourceType: drama.sourceType === "creator" ? "creator" : "platform",
    title: drama.title,
    categoryId: Number(drama.categoryId) || 0,
    categoryCode: (drama.categoryCode || "").trim(),
    category: categoryFromDrama(drama),
    description: drama.intro || drama.shareDesc || drama.shareTitle || "",
    coverUrl: drama.coverUrl,
    carouselImageUrl: drama.carouselImageUrl || drama.coverUrl,
    rating: 0,
    totalEpisodes: drama.totalEpisodes,
    unlockedCount: Number(drama.unlockedCount) || 0,
    lastEpisodeNo: Number(drama.lastEpisodeNo) || 0,
    lastEpisodeTitle: drama.lastEpisodeTitle || "",
    views: Number(drama.viewCount) || 0,
    weekViews: Number(drama.weekViewCount) || 0,
    monthViews: Number(drama.monthViewCount) || 0,
    isCarousel: Boolean(drama.isCarousel),
    isHot: Boolean(drama.isHot),
    hotRank: Number(drama.hotRank) || 0,
    tagHot: Number(drama.viewCount) > 0,
    tagNew: isNewDrama(drama.createdAt),
    createdAt: drama.createdAt,
    updatedAt: drama.updatedAt,
    latestEpisodeAt: drama.latestEpisodeAt,
    episodes,
  };
}

export function mapLibraryRows(library: MemberDramaLibrary) {
  return {
    recent: (library.recent || []).map(mapDramaCard),
    favorites: (library.favorites || []).map(mapDramaCard),
  };
}
